const DateFormat = require('@/lib/Dates/DateFormat');
const directDebitGuaranteeText = require('@/lib/PaymentMethods/GoCardless/directDebitGuaranteeText');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PrintDriverRefs = require('@/lib/Printing/PrintDriverRefs');
const PrintJobOptions = require('./PrintJobOptions');
const PrintType = require('@/lib/Printing/PrintType');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const { cloneDeep } = require('@/functions/clone');
const { getAskEarlyFieldInstancesByLine } = require('@/lib/CustomFields');
const { getPaymentMethodNameForReceipt } = require('@/lib/Payments');
const { orderIsCrossBranch } = require('@/functions/orders');
const { safeMultiply } = require('@/functions/math');
const { sortOrderLines } = require('@/lib/Orders/orderLineFunctions');

/**
 * Handles all business logic around what we print and when.
 * The logic of printing itself is now handled by the Printer function.
 */
class ReceiptPrinter {
    /**
     * @param {AppConfig} appConfig
     * @param {CurrentDevice} currentDevice
     * @param {LogoService} logoService
     * @param {Printer} printer
     * @param {TemplateRepository} templateRepository
     * @param {TradingSessionManager} tradingSessionManager
     * @param {UserService} userService
     */
    constructor(
        appConfig,
        currentDevice,
        logoService,
        printer,
        templateRepository,
        tradingSessionManager,
        userService,
    ) {
        this.appConfig = appConfig;
        this.currentDevice = currentDevice;
        this.logoService = logoService;
        this.printer = printer;
        this.templateRepository = templateRepository;
        this.tradingSessionManager = tradingSessionManager;
        this.userService = userService;

        this.log = logFactory('ReceiptPrinter');
    }

    /**
     * Returns the data that should be supplied to every print template.
     * Note: Do not store the result of this as it includes the current time. Call the function each time it is needed.
     *
     * @return {object}
     */
    async getDefaultPrintTemplateData() {
        const printableLogo = await this.logoService.getPrintableLogoUrl(this.userService.getOrderBranchID());

        return {
            branch: this.userService.currentBranch,
            company: this.userService.company,
            currentUser: this.userService.currentUser,
            date: new Date(),
            device: this.currentDevice.device,
            displayLogo: printableLogo !== null,
            PaymentMethodRef,
            printableLogo,
            companyTaxRegistration: this.userService.company.getTaxRegistrationCodeForCountry(
                this.userService.currentBranch.country
            ),

            // Make DateFormat available in the print templates.
            DateFormat,

            // Make safeMultiply function available in the print templates.
            safeMultiply,

            // Keep the previous 'user' property available in the template for BC because the receipt template in app
            // accesses it.
            // TODO: When receipt templates are moved to epos repo, rename this to userService and update templates.
            user: this.userService,
        };
    }

    /**
     * Returns the HTML string for the given template. If a custom template was loaded from the API it returns that,
     * otherwise it returns the bundled template content.
     *
     * @param {string} templateName
     * @param {string} localTemplatePath
     *
     * @return {Promise<string>}
     */
    getPrintTemplateContent(templateName, localTemplatePath) {
        return this.templateRepository
            .find(templateName, PrintDriverRefs.PRINT)
            .then((template) => template.content)
            .catch(() => {
                const templateContent = this.printer.getLocalTemplateContent(localTemplatePath);
                if (templateContent) {
                    this.log.debug(`No custom template ${templateName} exists. Using the default ${localTemplatePath}`);
                    return templateContent;
                }

                throw new Error(`Template ${templateName} was not found and no local template ${localTemplatePath} exists.`);
            });
    }

    /**
     * @param {Order} orderToPrint
     * @param {boolean} hasSouvenirPrinter
     *
     * @return {Promise}
     */
    async printGiftAidDeclaration(orderToPrint, hasSouvenirPrinter) {
        const opts = new PrintJobOptions(
            PrintType.GIFT_AID_DECLARATION,
            {
                openCashDrawer: false,
                addFooterDot: false,
            }
        );

        let templateName = hasSouvenirPrinter
            ? 'standalone-gift-aid-declaration-souvenir'
            : 'standalone-gift-aid-declaration';

        // Disable this functionality of loading a custom template until we need to use it.
        // const template = await getPrintTemplateContent(
        //    `orders.epos.${templateName}`,
        //    `print/templates/${templateName}.html`
        // );

        // Until we need custom template loading we will just hard code the local path but by then we may have
        // moved all custom templates in ProPoint anyway.
        templateName = 'print/templates/' + templateName + '.html';
        const template = this.printer.getLocalTemplateContent(templateName);

        this.log.debug('Loaded gift aid declaration template.', template);

        return this.printer.printTemplate(
            template,
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    order: orderToPrint,
                },
            ),
            opts
        );
    }

    /**
     * Print a no-sale receipt to trigger opening the cash drawer.
     *
     * This may be one of 3 things happening:
     * - The user is opening the drawer for an unknown reason (amount will be null).
     * - The user is adding cash to the drawer. They will have entered how much cash they intend to add and that will
     *   be the amount (although the actual amount added to the drawer may not match - all the we is the amount
     *   they entered!)
     * - The user is removing cash from the drawer. The amount will be int 0 and the receipt will print a blank line
     *   for the user to write how much cash they took.
     *
     * @param {string} title Text to use as the title of the receipt.
     * @param {?number} amount Will be null if no sale. int 0 if removing cash from the drawer. Otherwise the amount
     *     they said they would be be added to the drawer.
     * @param {?string} [narrative] Optional extra information provided for opening the drawer.
     *
     * @return {Promise}
     */
    async printNoSale(
        title,
        amount = null,
        narrative = null
    ) {
        this.log.debug('Print no sale');

        const opts = new PrintJobOptions(PrintType.NO_SALE);

        return this.printer.printLocalTemplate(
            'print/templates/no-sale.html',
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    amount,
                    isNoSale: amount === null,
                    title,
                    narrative,
                }
            ),
            opts
        );
    }

    /**
     * Print a receipt for an order before the order is finished (a bill - likely for table service).
     *
     * @param {Order} order
     *
     * @return {Promise}
     */
    async printBill(order) {
        const opts = new PrintJobOptions(PrintType.BILL);

        return this.printer.printLocalTemplate(
            'print/templates/bill.html',
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    order,
                    orderLines: this.prepareOrderLinesForReceipt(order, order.getLines()),
                }
            ),
            opts
        );
    }

    /**
     * Print the receipt for an order adjustment.
     *
     * @param {OrderAdjustmentResponse} orderAdjustmentResponse
     * @param {{isDuplicate?:boolean}} additionalData Data to be sent to the template.
     *
     * @return {Promise}
     */
    async printOrderAdjustmentReceipt(orderAdjustmentResponse, additionalData) {
        this.log.debug('Print order adjustment', cloneDeep(orderAdjustmentResponse));

        const opts = new PrintJobOptions(
            additionalData.isDuplicate ? PrintType.RECEIPT_DUPLICATE : PrintType.RECEIPT,
            {
                openCashDrawer: !additionalData.isDuplicate,
            },
        );

        // Separate out the removed and added lines from the adjustment order.
        let removedLines = orderAdjustmentResponse.adjustmentOrder.items.filter((i) => i.qty < 0);
        let addedLines = orderAdjustmentResponse.adjustmentOrder.items.filter((i) => i.qty >= 0);

        // Anything that refers to 'order' wants the order that just happened, so set that.
        const order = orderAdjustmentResponse.adjustmentOrder;

        if (order.greekTaxLine) {
            opts.classes.push('via-sign-pro');
        }

        // For an adjustment order we need to check all the added, removed, and remaining lines
        // to see if this is a cross branch order.
        const isCrossBranch = orderIsCrossBranch(
            order.orderBranchID,
            order,
            [
                // Lines that were removed.
                ...removedLines,
                // Lines that were added + any lines that remained untouched.
                ...orderAdjustmentResponse.adjustedOrder.getLines(),
            ]
        );

        return this.printer.printLocalTemplate(
            'print/templates/adjustment-receipt.html',
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    addedLines: this.prepareOrderLinesForReceipt(order, addedLines, isCrossBranch),
                    adjustedOrder: orderAdjustmentResponse.adjustedOrder,
                    adjustedOrderLines: this.prepareOrderLinesForReceipt(
                        order,
                        orderAdjustmentResponse.adjustedOrder.getLines(),
                        isCrossBranch,
                    ),
                    adjustmentOrder: orderAdjustmentResponse.adjustmentOrder,
                    order,
                    orderBranch: order.orderBranchID ? this.userService.getBranchByID(order.orderBranchID) : null,
                    originalOrder: orderAdjustmentResponse.originalOrder,
                    removedLines: this.prepareOrderLinesForReceipt(order, removedLines, isCrossBranch),
                    showPaymentDates: true, // Always show payment dates on adjustment receipts.
                    getPaymentMethodNameForReceipt,
                },
                additionalData
            ),
            opts
        );
    }

    /**
     * @private
     *
     * Do some processing of order lines before they can be printed on a receipt.
     * - Sorts the line by branchID.
     * - Sets the printNewBranchNameOnReceipt property so if there are multiple branches in the order
     *   the branch name gets printed to show which branch each group of lines is from.
     * Returns a new array of lines.
     *
     * @param {Order} order
     * @param {OrderLine[]} orderLines
     * @param {boolean|null} [isCrossBranch]
     *
     * @return {OrderLine[]}
     */
    prepareOrderLinesForReceipt(order, orderLines, isCrossBranch = null) {
        // Copy the lines into a new array so we don't modify the original array.
        // Note that the lines themselves are still the same objects.
        const lines = [...orderLines];

        // Check if this order contains any items from a branch other than the branch that created it.
        // isCrossBranch may be provided as a param if we already know the answer.
        if (isCrossBranch === null) {
            isCrossBranch = orderIsCrossBranch(order.orderBranchID, order, orderLines);
        }

        // Sort the lines so that child lines come under their parent lines.
        sortOrderLines(lines);

        // Sort lines by branch name.
        lines.sort((a, b) => a.branch.name.localeCompare(b.branch.name));

        // Set the lines where a new branch starts to display the branch name.
        // This could be done in the template, but it is calculated here to work around limitations of Angular.
        let lastBranchID = null;
        lines.forEach((line) => {
            if (
                this.userService.company.settings.printBranchNameOnProPointReceipts
                || isCrossBranch
            ) {
                // Either the company wants to always print branch names on receipts
                // or this is a cross branch order.
                // Set the flag to print the branch name if this is the first item or the first item from this branch.
                line.printNewBranchNameOnReceipt = !lastBranchID || line.branchID !== lastBranchID;
            }
            lastBranchID = line.branchID;
        });

        return lines;
    }

    /**
     * @param {Order} order
     * @param {{isDuplicate?:boolean}} additionalData Data to be sent to the template.
     */
    async printOrder(order, additionalData) {
        this.log.debug('Print order', { orderID: order.ID, bookingRef: order.bookingRef, date: order.date });

        const opts = new PrintJobOptions(
            additionalData.isDuplicate ? PrintType.RECEIPT_DUPLICATE : PrintType.RECEIPT,
            {
                openCashDrawer: !additionalData.isDuplicate,
            }
        );
        if (order.greekTaxLine) {
            opts.classes.push('via-sign-pro');
        }

        const templateContent = await this.getPrintTemplateContent(
            'orders.epos.receipt',
            'print/templates/receipt.html'
        );
        this.log.debug('Loaded receipt template.', templateContent);

        const askEarlyFieldInstancesByLine = getAskEarlyFieldInstancesByLine(
            order.fields,
            true // Only return fields to show to customers.
        );

        return this.printer.printTemplate(
            templateContent,
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    order,
                    orderLines: this.prepareOrderLinesForReceipt(order, order.getLines()),
                    orderBranch: order.orderBranchID ? this.userService.getBranchByID(order.orderBranchID) : null,
                    askEarlyFieldInstancesByLine,
                    safeMultiply,
                    showPaymentDates: order.hasPaymentsAfterOrderDate(),
                    getPaymentMethodNameForReceipt,
                },
                additionalData
            ),
            opts
        );
    }

    /**
     * Choose the template we need to use and deal with each orderLine in turn.
     *
     * @param {Order} orderToPrint
     * @param {boolean} hasSouvenirPrinter
     *
     * @return {Promise}
     */
    async printIndividualTickets(orderToPrint, hasSouvenirPrinter) {
        const opts = new PrintJobOptions(
            PrintType.TICKETS_INDIVIDUAL,
            {
                openCashDrawer: false,
                addFooterDot: false,
            }
        );

        const templateName = hasSouvenirPrinter ? 'ticket-souvenir' : 'ticket';
        const template = await this.getPrintTemplateContent(
            `orders.epos.${templateName}`,
            `print/templates/${templateName}.html`
        );

        let order = cloneDeep(orderToPrint);
        order.items = order.items.filter((orderItem) => orderItem.item.printTicketPerInstance);

        // Record the ticket printing. We want to send an array of [itemInstanceRef, ticketID] pairs.
        // By generating this event here we don't have to duplicate code for standard and Boca tickets.
        let printedTickets = [];
        order.items.forEach((item) => {
            if (item.iteminstances) {
                item.iteminstances.forEach((itemInstance) => {
                    printedTickets.push({ itemInstanceRef: itemInstance.itemInstanceRef, itemID: item.itemID });
                });
            }
        });
        this.tradingSessionManager.saveEvent(
            (new TradingSessionEvent(TradingSessionEventType.TICKETS_PRINTED))
                .setData(printedTickets)
        );

        if (hasSouvenirPrinter) {
            // The souvenir ticket templates have been written so that they do the looping to compile all the tickets
            // into a single printable page.
            return this.printer.printTemplate(
                template,
                Object.assign(
                    await this.getDefaultPrintTemplateData(),
                    {
                        order,
                    }
                ),
                opts
            );
        }

        // Process each orderLine and print a ticket per instance.
        return Promise.all(order.items.map((orderItem) => {
            return this.printIndividualTicketLine(orderItem, order, template);
        }));
    }

    /**
     * Choose the template we need to use and deal with each orderLine in turn.
     *
     * @param {Order} orderToPrint
     * @param {boolean} hasSouvenirPrinter
     *
     * @return {Promise}
     */
    async printGroupTickets(orderToPrint, hasSouvenirPrinter) {
        const opts = new PrintJobOptions(
            PrintType.TICKETS_GROUP,
            {
                openCashDrawer: false,
                addFooterDor: false,
            }
        );

        const templateName = hasSouvenirPrinter ? 'group-ticket-souvenir' : 'group-ticket';
        let template = await this.getPrintTemplateContent(
            `orders.epos.${templateName}`,
            `print/templates/${templateName}.html`
        );

        let order = cloneDeep(orderToPrint);
        order.items = order.items.filter((orderItem) => orderItem.item.printTicketPerInstance);

        if (hasSouvenirPrinter) {
            // The souvenir ticket templates have been written so that they do the looping to compile all the tickets
            // into a single printable page.

            return this.printer.printTemplate(
                template,
                Object.assign(
                    await this.getDefaultPrintTemplateData(),
                    {
                        order,
                    }
                ),
                opts
            );
        }

        // Process each orderLine.
        return Promise.all(
            order.items.map((orderItem) => this.printGroupTicketLine(orderItem, order, opts, template))
        );
    }

    /**
     * @private
     *
     * Process each instance of each ticket.
     *
     * @param {OrderLine} orderItem
     * @param {Order} order
     * @param {string} template
     */
    printIndividualTicketLine(orderItem, order, template) {
        return Promise.all(
            orderItem.iteminstances.map(
                (itemInstance) => this.printIndividualTicket(
                    itemInstance,
                    orderItem,
                    order,
                    template
                )
            )
        );
    }

    /**
     * @private
     *
     * Generate a single group ticket for the orderLine.
     *
     * @param {OrderLine} orderItem
     * @param {Order} order
     * @param {PrintJobOptions} opts
     * @param {string} template
     *
     * @return {Promise}
     */
    async printGroupTicketLine(orderItem, order, opts, template) {
        let ticketData = {
            numPeople: orderItem.qty * orderItem.item.people,
            ref: order.bookingRef,
        };

        return this.printer.printTemplate(
            template,
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    ticketData,
                    orderItem,
                    order,
                }
            ),
            opts
        );
    }

    /**
     * @private
     *
     * Send an individual ticket instance to the Printer ready to be compiled and added to the print queue.
     *
     * @param {object} itemInstance
     * @param {OrderLine} orderItem
     * @param {Order} order
     * @param {string} template The name of the local template to use.
     *
     * @Return {Promise}
     */
    async printIndividualTicket(itemInstance, orderItem, order, template) {
        let ticketData = {
            numPeople: orderItem.item.people,
            ref: itemInstance.itemInstanceRef,
        };

        // Each ticket needs its own onSuccess/onFailure callbacks.
        const opts = new PrintJobOptions(
            PrintType.TICKETS_INDIVIDUAL,
            {
                openCashDrawer: false,
                addFooterDot: false,
            }
        );

        return this.printer.printTemplate(
            template,
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    ticketData,
                    orderItem,
                    order,
                }
            ),
            opts
        );
    }

    /**
     * @param {KitchenOrder} kitchenOrder
     * @param {?{added: KitchenOrderItem[], removed: KitchenOrderItem[]}} [diff]
     * @param {PrintJobOptions} opts
     *
     * @return {Promise}
     */
    async printKitchenOrder(kitchenOrder, diff, opts = null) {
        diff = diff === undefined ? null : diff;

        this.log.debug('Print kitchen order', { order: kitchenOrder, diff, opts });

        if (!opts) {
            opts = new PrintJobOptions(PrintType.KITCHEN_ORDER);
        }

        return this.printer.printLocalTemplate(
            'print/templates/kitchen-receipt.html',
            {
                order: kitchenOrder,
                visibleItems: kitchenOrder.getVisibleItems(),
                diff,
            },
            opts
        );
    }

    /**
     * This is for printing a receipt (called a "voucher") record from the old Verifone VX820 IP (Evo) integration.
     *
     * @param {VerifoneVoucherRecord} record
     * @param {PrintJobOptions} [opts]
     *
     * @return {Promise}
     */
    printVerifoneVoucher(record, opts) {
        opts = new PrintJobOptions(PrintType.CARD_RECEIPT, {}, opts);

        this.log.debug('Print verifone voucher', { record });

        return this.printer.printLocalTemplate(
            'print/templates/verifone-voucher.html',
            {
                record,
            },
            opts
        );
    }

    /**
     * @param {PrintJobOptions} opts
     *
     * @return {Promise}
     */
    printBlankPage(opts) {
        return this.printer.printContent('', opts);
    }

    /**
     * @param {object} data
     * @param {PrintJobOptions} [opts]
     *
     * @returns {Promise}
     */
    printPayyoCardReceipt(data, opts) {
        opts = new PrintJobOptions(PrintType.CARD_RECEIPT, {}, opts);

        return this.printer.printLocalTemplate(
            'print/templates/payyo-card-receipt.html',
            data,
            opts
        );
    }

    /**
     * @param {string} receiptHtml
     * @param {PrintJobOptions} [opts]
     *
     * @returns {Promise}
     */
    printVerifoneCardReceipt(receiptHtml, opts) {
        opts = new PrintJobOptions(PrintType.CARD_RECEIPT, {}, opts);

        // Verifone receipts that came from XML don't use the .print-content class. They only use
        // .print-structure because the verifone-xml-receipt-renderer applies styles to the text already
        // to make it the correct size and spacing to meet Verifone's requirements.
        return this.printer.printTemplate('<div id="verifone-card-receipt" class="print-structure">' + receiptHtml + '</div>', {}, opts);
    }

    /**
     * @param {string} receiptHtml
     * @param {PrintJobOptions} [opts]
     *
     * @returns {Promise}
     */
    printWorldpayCardReceipt(receiptHtml, opts) {
        opts = new PrintJobOptions(PrintType.CARD_RECEIPT, {}, opts);

        return this.printer.printTemplate('<div id="worldpay-card-receipt" class="print-content">' + receiptHtml + '</div>', {}, opts);
    }

    /**
     * @param {string} receiptHtml
     * @param {PrintJobOptions} [opts]
     *
     * @returns {Promise}
     */
    printPaymentExpressCardReceipt(receiptHtml, opts) {
        opts = new PrintJobOptions(PrintType.CARD_RECEIPT, {}, opts);

        return this.printer.printTemplate('<div id="payment-express-card-receipt" class="print-content">' + receiptHtml + '</div>', {}, opts);
    }

    /**
     * @private
     *
     * @param {SoldGiftVoucher} soldGiftVoucher
     *
     * @return {Promise}
     */
    async printGiftVoucher(soldGiftVoucher) {
        const opts = new PrintJobOptions(
            PrintType.GIFT_VOUCHER,
            {
                openCashDrawer: false,
            }
        );

        return this.printer.printLocalTemplate(
            'print/templates/sold-gift-voucher.html',
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                {
                    soldGiftVoucher,
                }
            ),
            opts
        );
    }

    /**
     * @param {SoldGiftVoucher[]} soldGiftVouchers
     *
     * @return {Promise<any[]>}
     */
    async printGiftVouchers(soldGiftVouchers) {
        return Promise.all(
            soldGiftVouchers.map((soldGiftVoucher) => this.printGiftVoucher(soldGiftVoucher))
        );
    }

    /**
     * @param {object} templateData
     *
     * @return {Promise}
     */
    async printTestPage(templateData) {
        const opts = new PrintJobOptions(PrintType.TEST);

        return this.printer.printLocalTemplate(
            'print/templates/printer-test.html',
            Object.assign(
                await this.getDefaultPrintTemplateData(),
                templateData
            ),
            opts
        );
    }

    /**
     * @param {Waiver} waiver
     *
     * @return {Promise}
     */
    async printWaiverLinkSlip(waiver) {
        const opts = new PrintJobOptions(
            PrintType.WAIVER_LINK_SLIP
        );

        return this.printer.printLocalTemplate(
            'print/templates/waiver-link-slip.html',
            {
                ...await this.getDefaultPrintTemplateData(),

                waiverName: waiver.name,
                waiverLink: waiver.getWaiverLink(this.appConfig),
            },
            opts
        );
    }

    /**
     * @private
     *
     * Are any of the items in the order lines given printable as tickets?
     *
     * @param {OrderLine[]} orderItems
     * @returns {boolean}
     */
    canPrintTicketsForOrderLines(orderItems) {
        return !!orderItems.find(
            (line) => line.item && line.item.canPrintTicket
        );
    }

    /**
     * @private
     *
     * @param {Order} order
     * @return {boolean}
     */
    canPrintTicketsForOrder(order) {
        return order.items && this.canPrintTicketsForOrderLines(order.items);
    }

    async printDirectDebitGuarantee() {
        const opts = new PrintJobOptions(
            PrintType.DIRECT_DEBIT_GUARANTEE
        );

        const defaultPrintData = await this.getDefaultPrintTemplateData();

        return this.printer.printLocalTemplate(
            'print/templates/direct-debit-guarantee.html',
            {
                ...defaultPrintData,
                directDebitGuaranteeText: directDebitGuaranteeText(defaultPrintData.company.name),
            },
            opts
        );
    }
}

module.exports = ReceiptPrinter;
